// Author: Akarsh Singh | 2026-09-15 | Identity-scoped catalogue snapshots and mutation epochs.
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

interface Entry {
  subject: BehaviorSubject<any>;
  updatedAt: number;
  inFlight?: Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class ServiceCatalogueSWRService {
  private cache = new Map<string, Entry>();
  private epoch = 0;
  private readonly ttlMs = 15000;

  keyOf(viewer: string, role: string, session: string, section: string, query: unknown): string {
    return JSON.stringify([viewer, role, session, section, query]);
  }
  private entry(key: string): Entry {
    let entry = this.cache.get(key);
    if (!entry) {
      entry = { subject: new BehaviorSubject(null), updatedAt: 0 };
      this.cache.set(key, entry);
    }
    return entry;
  }
  stream$(key: string) { return this.entry(key).subject.asObservable(); }
  getFresh(key: string): any {
    const entry = this.entry(key);
    if (Date.now() - entry.updatedAt < this.ttlMs) return entry.subject.value;
    if (entry.subject.value !== null) entry.subject.next(null);
    return null;
  }
  refresh(key: string, fetcher: () => Promise<any>): Promise<void> {
    const entry = this.entry(key);
    if (entry.inFlight) return entry.inFlight;
    const epoch = this.epoch;
    const task = Promise.resolve().then(fetcher).then(data => {
      if (epoch !== this.epoch) return;
      entry.updatedAt = Date.now();
      entry.subject.next(data);
    }).finally(() => {
      if (entry.inFlight === task) entry.inFlight = undefined;
    });
    entry.inFlight = task;
    return task;
  }
  invalidateAll(): void {
    this.epoch++;
    this.cache.forEach(entry => {
      entry.updatedAt = 0;
      entry.inFlight = undefined;
      entry.subject.next(null);
    });
  }
  isMutation(url: string, method: string): boolean {
    if (/^(GET|HEAD|OPTIONS)$/i.test(method)) return false;
    // Catalogue/supplier metadata is edited from several portal surfaces.
    // Broad invalidation is intentional; reads and document generation are excluded.
    const path = url.split('?')[0];
    if (path.includes('/services/cart/')) return false;
    if (!/\/(services|settings|users|supplier|buyer|serviceCategory)\//i.test(path)) return false;
    const action = path.split('/').pop() || '';
    return !/^(fetch|get|search|filter|generate|export|verify|check)/i.test(action);
  }
}
